import { DialogCloseEvent } from './dialog.js';
import { clamp } from './utilities.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const states = new WeakMap();

/** @type {HTMLElement | null} */
let activeWrapper = null;

function isDesktop() {
  return window.matchMedia('(min-width: 750px)').matches;
}

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function getWrapper(target) {
  if (!(target instanceof Element)) return null;
  return target.closest('.dialog-zoomed-gallery .product-media__drag-zoom-wrapper');
}

/** @param {HTMLElement} wrapper */
function getState(wrapper) {
  let state = states.get(wrapper);

  if (!state) {
    state = {
      scale: MIN_ZOOM,
      x: 0,
      y: 0,
      dragging: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      startTranslateX: 0,
      startTranslateY: 0,
    };
    states.set(wrapper, state);
  }

  return state;
}

/**
 * @param {HTMLElement} wrapper
 * @param {ReturnType<typeof getState>} state
 */
function applyState(wrapper, state) {
  const image = wrapper.querySelector('img.product-media__image');
  if (!(image instanceof HTMLImageElement)) return;

  state.scale = clamp(state.scale, MIN_ZOOM, MAX_ZOOM);

  if (state.scale <= MIN_ZOOM + 0.001) {
    state.scale = MIN_ZOOM;
    state.x = 0;
    state.y = 0;
  } else {
    const rect = wrapper.getBoundingClientRect();
    const naturalWidth = image.naturalWidth || rect.width;
    const naturalHeight = image.naturalHeight || rect.height;
    const imageRatio = naturalWidth / naturalHeight;
    const wrapperRatio = rect.width / rect.height;

    let fittedWidth;
    let fittedHeight;

    if (imageRatio > wrapperRatio) {
      fittedWidth = rect.width;
      fittedHeight = fittedWidth / imageRatio;
    } else {
      fittedHeight = rect.height;
      fittedWidth = fittedHeight * imageRatio;
    }

    const overflowX = Math.max(0, fittedWidth * state.scale - rect.width);
    const overflowY = Math.max(0, fittedHeight * state.scale - rect.height);
    const maxX = overflowX / 2 / state.scale;
    const maxY = overflowY / 2 / state.scale;

    state.x = clamp(state.x, -maxX, maxX);
    state.y = clamp(state.y, -maxY, maxY);
  }

  wrapper.style.setProperty('--drag-zoom-scale', state.scale.toString());
  wrapper.style.setProperty('--drag-zoom-translate-x', state.x + 'px');
  wrapper.style.setProperty('--drag-zoom-translate-y', state.y + 'px');
  wrapper.toggleAttribute('data-zoomed', state.scale > MIN_ZOOM + 0.01);
  wrapper.toggleAttribute('data-dragging', state.dragging);
}

/**
 * @param {HTMLElement} wrapper
 * @param {number} targetScale
 * @param {number} clientX
 * @param {number} clientY
 */
function zoomAtPoint(wrapper, targetScale, clientX, clientY) {
  const state = getState(wrapper);
  const oldScale = state.scale;
  const nextScale = clamp(targetScale, MIN_ZOOM, MAX_ZOOM);

  if (nextScale <= MIN_ZOOM + 0.001) {
    state.scale = MIN_ZOOM;
    state.x = 0;
    state.y = 0;
    applyState(wrapper, state);
    return;
  }

  const rect = wrapper.getBoundingClientRect();
  const distanceX = clientX - (rect.left + rect.width / 2);
  const distanceY = clientY - (rect.top + rect.height / 2);
  const scaleDelta = nextScale / oldScale - 1;

  state.scale = nextScale;
  state.x -= (distanceX * scaleDelta) / nextScale;
  state.y -= (distanceY * scaleDelta) / nextScale;
  applyState(wrapper, state);
}

document.addEventListener(
  'wheel',
  (event) => {
    if (!isDesktop()) return;

    const wrapper = getWrapper(event.target);
    if (!wrapper) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const state = getState(wrapper);
    const factor = Math.exp(-event.deltaY * 0.0015);
    zoomAtPoint(wrapper, state.scale * factor, event.clientX, event.clientY);
  },
  { passive: false, capture: true }
);

document.addEventListener(
  'pointerdown',
  (event) => {
    if (!isDesktop() || event.button !== 0) return;

    const wrapper = getWrapper(event.target);
    if (!wrapper) return;

    const state = getState(wrapper);
    if (state.scale <= MIN_ZOOM) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    activeWrapper = wrapper;
    state.dragging = true;
    state.pointerId = event.pointerId;
    state.startX = event.clientX;
    state.startY = event.clientY;
    state.startTranslateX = state.x;
    state.startTranslateY = state.y;
    wrapper.setPointerCapture(event.pointerId);
    applyState(wrapper, state);
  },
  true
);

document.addEventListener(
  'pointermove',
  (event) => {
    if (!activeWrapper) return;

    const state = getState(activeWrapper);
    if (!state.dragging || state.pointerId !== event.pointerId) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    state.x = state.startTranslateX + (event.clientX - state.startX) / state.scale;
    state.y = state.startTranslateY + (event.clientY - state.startY) / state.scale;
    applyState(activeWrapper, state);
  },
  true
);

function endDrag(event) {
  if (!activeWrapper) return;

  const state = getState(activeWrapper);
  if (state.pointerId !== event.pointerId) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  if (activeWrapper.hasPointerCapture(event.pointerId)) {
    activeWrapper.releasePointerCapture(event.pointerId);
  }

  state.dragging = false;
  state.pointerId = null;
  applyState(activeWrapper, state);
  activeWrapper = null;
}

document.addEventListener('pointerup', endDrag, true);
document.addEventListener('pointercancel', endDrag, true);

document.addEventListener(
  'click',
  (event) => {
    if (!isDesktop() || !getWrapper(event.target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

document.addEventListener(
  'dblclick',
  (event) => {
    if (!isDesktop()) return;

    const wrapper = getWrapper(event.target);
    if (!wrapper) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const state = getState(wrapper);
    zoomAtPoint(wrapper, state.scale > MIN_ZOOM + 0.05 ? MIN_ZOOM : 2, event.clientX, event.clientY);
  },
  true
);

function resetAllZoom() {
  document
    .querySelectorAll('.dialog-zoomed-gallery .product-media__drag-zoom-wrapper')
    .forEach((wrapper) => {
      if (!(wrapper instanceof HTMLElement)) return;

      states.delete(wrapper);
      wrapper.style.setProperty('--drag-zoom-scale', '1');
      wrapper.style.setProperty('--drag-zoom-translate-x', '0px');
      wrapper.style.setProperty('--drag-zoom-translate-y', '0px');
      wrapper.removeAttribute('data-zoomed');
      wrapper.removeAttribute('data-dragging');
    });

  activeWrapper = null;
}

window.addEventListener(DialogCloseEvent.eventName, resetAllZoom);


// Product image context-menu protection — 2026-08-26
const protectedProductImageSelector =
  '.product-information img.product-media__image, .dialog-zoomed-gallery img.product-media__image';

function isProtectedProductImage(target) {
  return target instanceof Element && Boolean(target.closest(protectedProductImageSelector));
}

document.addEventListener(
  'contextmenu',
  (event) => {
    if (!isProtectedProductImage(event.target)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

document.addEventListener(
  'dragstart',
  (event) => {
    if (!isProtectedProductImage(event.target)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);
